import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, NOMBRES, seededRandom } from "../spec";

const SHEET = "LG Créditos";
const TABLE = "Solicitudes";
const BURO = ["Bueno", "Bueno", "Regular", "Malo"] as const;

const rnd = seededRandom(7310);
// Solicitante, Edad, Ingreso mensual, Antigüedad laboral, Buró, Deuda actual
const ROWS = NOMBRES.slice(0, 32).map((nombre) => {
  const ingreso = rnd.round(8000, 60000, 500);
  const deuda = rnd.next() < 0.25 ? 0 : rnd.round(0, ingreso * 4, 500);
  return [
    nombre,
    rnd.int(17, 64),
    ingreso,
    rnd.int(0, 12),
    rnd.pick(BURO),
    deuda,
  ] as [string, number, number, number, string, number];
});

type Row = (typeof ROWS)[number];
const basicos = (r: Row) => r[1] >= 18 && r[2] >= 12000;
const alerta = (r: Row) => r[4] === "Malo" || r[5] > r[2] * 3;

export const spec: FuncionProjectSpec = {
  projectId: "funciones-y-o-no",
  sheets: [
    {
      name: SHEET,
      title: "Aprobación de créditos",
      subtitle: "Solicitudes de crédito de nómina — escribe tus fórmulas en las columnas amarillas",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: [
            "Solicitante",
            "Edad",
            "Ingreso mensual",
            "Antigüedad laboral",
            "Buró",
            "Deuda actual",
          ],
          rows: ROWS,
          answerColumns: [
            "Cumple básicos",
            "Alerta",
            "Sin deuda",
            "Decisión",
            "Revisión",
            "Tasa preferente",
          ],
          formats: { "Ingreso mensual": "$#,##0", "Deuda actual": "$#,##0" },
        },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Cumple básicos", fns: ["AND"],
      expected: ROWS.map(basicos),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Alerta", fns: ["OR"],
      expected: ROWS.map(alerta),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Sin deuda", fns: ["NOT"],
      expected: ROWS.map((r) => !(r[5] > 0)),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Decisión", fns: ["IF", "AND"],
      expected: ROWS.map((r) => (basicos(r) && r[4] !== "Malo" ? "Aprobado" : "Rechazado")),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Revisión", fns: ["IF", "OR"],
      expected: ROWS.map((r) => (r[4] === "Regular" || r[3] < 1 ? "Revisar" : "OK")),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Tasa preferente", fns: ["IF", "AND", "OR"],
      expected: ROWS.map((r) =>
        r[4] === "Bueno" && (r[3] >= 5 || r[2] >= 40000) ? "Sí" : "No"
      ),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-y-o-no",
  title: "Y, O y NO: Aprobación de créditos",
  subtitle: "Varias condiciones a la vez",
  description:
    "Trabajas en el área de crédito de una financiera. Cada solicitud debe cumplir varias reglas al mismo tiempo, o disparar una alerta si se cumple alguna señal de riesgo.\n\nPrimero usarás Y, O y NO solas (devuelven VERDADERO o FALSO) y después dentro de SI para tomar la decisión final.",
  topics: [
    "Y: todas las condiciones",
    "O: al menos una condición",
    "NO: invertir una condición",
    "Y y O dentro de SI",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: ¿Cumple los requisitos básicos?",
      description:
        "En la hoja <strong>LG Créditos</strong>, columna <strong>Cumple básicos</strong>: usa <strong>Y</strong> para devolver VERDADERO solo si el solicitante tiene <strong>18 años o más</strong> y un <strong>ingreso mensual de $12,000 o más</strong>.",
      solution: [
        `Selecciona ${F("G5")}.`,
        "Y devuelve VERDADERO únicamente cuando todas sus condiciones son verdaderas.",
        `Escribe ${F("=Y([@Edad]>=18,[@[Ingreso mensual]]>=12000)")}.`,
      ],
      expectedResult: "VERDADERO / FALSO",
      finalFormula: "=Y([@Edad]>=18,[@[Ingreso mensual]]>=12000)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Señal de riesgo",
      description:
        "En la columna <strong>Alerta</strong>: usa <strong>O</strong> para devolver VERDADERO si el buró es <strong>Malo</strong> o si la <strong>deuda actual</strong> es mayor a <strong>3 veces el ingreso mensual</strong>.",
      solution: [
        `Selecciona ${F("H5")}.`,
        "O devuelve VERDADERO si al menos una de sus condiciones es verdadera.",
        `Escribe ${F('=O([@Buró]="Malo",[@[Deuda actual]]>[@[Ingreso mensual]]*3)')}.`,
      ],
      expectedResult: "VERDADERO / FALSO",
      finalFormula: '=O([@Buró]="Malo",[@[Deuda actual]]>[@[Ingreso mensual]]*3)',
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: ¿Está libre de deudas?",
      description:
        "En la columna <strong>Sin deuda</strong>: usa <strong>NO</strong> para devolver VERDADERO cuando la deuda actual <strong>no</strong> es mayor que 0.",
      solution: [
        `Selecciona ${F("I5")}.`,
        `NO invierte un resultado: ${F("NO(VERDADERO)")} es FALSO.`,
        `Escribe ${F("=NO([@[Deuda actual]]>0)")}.`,
      ],
      expectedResult: "VERDADERO / FALSO",
      finalFormula: "=NO([@[Deuda actual]]>0)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Decisión de crédito",
      description:
        conditions(
        "En la columna <strong>Decisión</strong>, escribe <strong>Aprobado</strong> solo si se cumplen <strong>las tres</strong> reglas:",
        [
          "Edad de <strong>18 años o más</strong>",
          "Ingreso mensual de <strong>$12,000 o más</strong>",
          "Buró <strong>distinto de Malo</strong>",
        ],
        "Si falta cualquiera, escribe <strong>Rechazado</strong>. Usa <strong>SI</strong> con <strong>Y</strong>."
      ),
      solution: [
        `Selecciona ${F("J5")}.`,
        "Y va dentro de SI, en el lugar de la prueba lógica.",
        `\"Distinto de\" se escribe ${F("<>")}.`,
        `Escribe ${F('=SI(Y([@Edad]>=18,[@[Ingreso mensual]]>=12000,[@Buró]<>"Malo"),"Aprobado","Rechazado")')}.`,
      ],
      expectedResult: "Aprobado / Rechazado",
      finalFormula:
        '=SI(Y([@Edad]>=18,[@[Ingreso mensual]]>=12000,[@Buró]<>"Malo"),"Aprobado","Rechazado")',
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: ¿Necesita revisión manual?",
      description:
        conditions(
        "En la columna <strong>Revisión</strong>, escribe <strong>Revisar</strong> si se cumple <strong>al menos una</strong> de estas condiciones:",
        [
          "Buró <strong>Regular</strong>",
          "Antigüedad laboral <strong>menor a 1 año</strong>",
        ],
        "En otro caso, escribe <strong>OK</strong>. Usa <strong>SI</strong> con <strong>O</strong>."
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        `Escribe ${F('=SI(O([@Buró]="Regular",[@[Antigüedad laboral]]<1),"Revisar","OK")')}.`,
      ],
      expectedResult: "Revisar / OK",
      finalFormula: '=SI(O([@Buró]="Regular",[@[Antigüedad laboral]]<1),"Revisar","OK")',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Tasa preferente",
      description:
        conditions(
        "En la columna <strong>Tasa preferente</strong>, escribe <strong>Sí</strong> cuando se cumplan <strong>las dos</strong> condiciones:",
        [
          "Buró <strong>Bueno</strong>",
          "<strong>5 o más</strong> años de antigüedad <em>o</em> ingreso de <strong>$40,000 o más</strong> (basta con una de las dos)",
        ],
        "En otro caso, <strong>No</strong>. Necesitas un <strong>O dentro de un Y</strong>, dentro de SI."
      ),
      solution: [
        `Selecciona ${F("L5")}.`,
        `Piensa la regla como: Bueno Y (antigüedad ≥ 5 O ingreso ≥ 40000). El paréntesis es un ${F("O(...)")} dentro de ${F("Y(...)")}.`,
        `Escribe ${F('=SI(Y([@Buró]="Bueno",O([@[Antigüedad laboral]]>=5,[@[Ingreso mensual]]>=40000)),"Sí","No")')}.`,
      ],
      expectedResult: "Sí / No",
      finalFormula:
        '=SI(Y([@Buró]="Bueno",O([@[Antigüedad laboral]]>=5,[@[Ingreso mensual]]>=40000)),"Sí","No")',
    },
  ],
};
